import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { VendorRole, VendorCategory } from '../types';

/**
 * Phase 1: Role is stored in localStorage as a dev/demo override.
 * Phase 2: Pull from Entra ID group claims in the MSAL token.
 *
 * localStorage key: vm_role_override
 * Value format: "VendorManager" | "DeptManager:Tech/IT" | "Viewer"
 */

export interface RoleContext {
  role: VendorRole;
  /** For DeptManager — which category they're scoped to */
  scopedCategory: VendorCategory | null;
  /** Can the current user see/manage contracts? */
  canManageContracts: boolean;
  /** Can the current user see/manage billing? */
  canManageBilling: boolean;
  /** Can the current user approve billing entries? */
  canApproveBilling: boolean;
  /** Can the current user approve contract auth workflows? */
  canApproveContracts: boolean;
  /** Can the current user edit vendors? */
  canEditVendors: boolean;
  /** Is this vendor visible to the current user? (category gate for DeptManager) */
  canSeeVendor: (categories: string[]) => boolean;
  /** Set role override (dev/demo only) */
  setRoleOverride: (raw: string) => void;
}

function parseRole(raw: string | null): { role: VendorRole; scopedCategory: VendorCategory | null } {
  if (!raw) return { role: 'VendorManager', scopedCategory: null };
  if (raw.startsWith('DeptManager:')) {
    const cat = raw.split(':')[1] as VendorCategory;
    return { role: 'DeptManager', scopedCategory: cat };
  }
  if (raw === 'Viewer') return { role: 'Viewer', scopedCategory: null };
  return { role: 'VendorManager', scopedCategory: null };
}

export function useVendorRole(): RoleContext {
  const { account } = useAuth();

  return useMemo(() => {
    const raw = localStorage.getItem('vm_role_override');
    const { role, scopedCategory } = parseRole(raw);

    const canManageContracts = role === 'VendorManager';
    const canManageBilling   = role !== 'Viewer';
    const canApproveBilling  = role === 'DeptManager';
    const canApproveContracts = role === 'DeptManager';
    const canEditVendors     = role !== 'Viewer';

    const canSeeVendor = (categories: string[]): boolean => {
      if (role === 'VendorManager') return true;
      if (role === 'Viewer' && !scopedCategory) return true;
      if (!scopedCategory) return true;
      return categories.includes(scopedCategory);
    };

    const setRoleOverride = (newRaw: string) => {
      if (!import.meta.env.DEV) return; // dev/demo only — no-op in production builds
      localStorage.setItem('vm_role_override', newRaw);
      window.location.reload();
    };

    return {
      role,
      scopedCategory,
      canManageContracts,
      canManageBilling,
      canApproveBilling,
      canApproveContracts,
      canEditVendors,
      canSeeVendor,
      setRoleOverride,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);
}
